import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLocationDealsList,
  fetchSessionRequest,
  fetchLocationDealsPrices,
} from '../../api';
import { prepareSearchRequest } from '../dealsList/dealsListSlice';
import { DEALS_PER_PAGE } from '../../constants/constants';

import { pushEvent } from '../../utilities/analytics';
import { LOAD_LOCATION_DEALS_EVENT, LOAD_OFFERS_ONTHEFLY_EVENT, LOAD_MORE_LOCATION_DEALS_FINISHED_EVENT } from '../../constants/analyticEvents';
import { formatStaticDealsParams } from '../../utilities/helpers';
import { FETCH_PING_STATUS } from '../../constants';

const initialState = {
  pending: true,
  error: false,
  list: [],
  pagination: {},
  session: {},
  sessionPending: false,
  isPricesFetched: false,
  hotelPrices: {
    offers: [],
    pending: false,
    error: false,
    pingStatusPending: false,
  },
  hideSoldOutDeals: true,// false, // changed to hide soldout deals by default
  showSoldOutAlert: false,
};

export const locationDealsSlice = createSlice({
  name: 'locationDeals',
  initialState,
  reducers: {
    getLocationDealsSuccess: (state, action) => {
      return {
        ...state,
        list: action.payload.hotels,
        pagination: action.payload.pagination,
        pending: false,
        error: false,
      };
    },
    getLocationDealsFailed: (state) => {
      return {
        ...state,
        pending: false,
        error: true,
      };
    },
    loadMoreLocationDealsSuccess: (state, action) => {
      return {
        ...state,
        list: [...state.list, ...action.payload.hotels],
        pagination: action.payload.pagination,
        pending: false,
        error: false,
      };
    },
    loadMoreLocationDealsFailed: (state) => {
      return state;
    },
    clearLocationDeals: (state) => {
      return {
        ...state,
        pending: true,
        error: false,
        list: [],
        pagination: {},
      };
    },
    setIsLoading: (state) => {
      return {
        ...state,
        pending: true,
      };
    },
    fetchSessionStart: (state) => {
      state.sessionPending = true;
    },
    fetchSessionSuccess: (state, action) => ({
      ...state,
      session: action.payload,
      sessionPending: false,
    }),
    setHotelPriceLoading: (state) => {
      state.hotelPrices.pending = true;
      state.hotelPrices.error = false;
    },
    fetchHotelPricesSuccess: (state, action) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        ...action.payload.data,
        pending: false,
      };
    },
    fetchHotelPricesFailed: (state) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        pending: false,
        error: true,
        pingStatusPending: false,
      };
    },
    fetchHotelPricesPaginationSuccess: (state, action) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        pagination: action.payload.data.pagination,
      }
    },
    loadMoreHotelPricesSuccess: (state, action) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        offers: [...state.hotelPrices.offers, ...action.payload.data.offers],
        pagination: action.payload.data.pagination,
        pending: false,
        error: false,
      };
    },
    clearHotelPrices: (state) => {
      state.hotelPrices.offers = [];
    },
    setFilters: (state, action) => {
      state.hotelPrices.filters = action.payload.sections;
    },
    setIsPricesFetched: (state, action) => ({
      ...state,
      isPricesFetched: action.payload,
    }),
    setHideSoldOutDeals: (state, action) => ({
      ...state,
      hideSoldOutDeals: action.payload,
    }),
    setShowSoldOutAlert: (state, action) => ({
      ...state,
      showSoldOutAlert: action.payload,
    }),
    setPingStatusPending: (state) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        pingStatusPending: true,
      };
    },
    clearPingStatusPending: (state) => {
      state.hotelPrices = {
        ...state.hotelPrices,
        pingStatusPending: false,
      };
    },
  },
});

export const {
  getLocationDealsSuccess,
  getLocationDealsFailed,
  loadMoreLocationDealsSuccess,
  loadMoreLocationDealsFailed,
  setIsLoading,
  fetchSessionStart,
  fetchSessionSuccess,
  fetchHotelPricesSuccess,
  fetchHotelPricesFailed,
  fetchHotelPricesPaginationSuccess,
  setHotelPriceLoading,
  loadMoreHotelPricesSuccess,
  clearHotelPrices,
  setFilters,
  clearLocationDeals,
  setIsPricesFetched,
  setHideSoldOutDeals,
  setShowSoldOutAlert,
  setPingStatusPending,
  clearPingStatusPending,
} = locationDealsSlice.actions;


export const fetchLocationDealsData = (params) => async (dispatch, getState) => {
  const { mainFilter, secondaryFilter, dealsList } = getState();
  const { fromwhere } = mainFilter.searchEngine;
  // const selectedDeparture = fromwhere.find(item => item.active) || fromwhere[0];
  const selectedDepartures = fromwhere.filter(item => item.active).map(dp => dp.id);
  const excludeHotelIds = dealsList.dealsList.map(({ hotel }) => hotel.provider_hotel_id)
  const data = {
    ...formatStaticDealsParams({
      destination: params.selectedDestination.id,
      filters: { ...secondaryFilter.data },
    }),
    fromwhere: selectedDepartures.length ? selectedDepartures : [fromwhere[0].id],
    excludeHotelIds,
    page: 1,
    offset: 0,
  };

  try {
    dispatch(setIsLoading());
    const response = await fetchLocationDealsList(data);
    pushEvent(LOAD_LOCATION_DEALS_EVENT, response);

    dispatch(getLocationDealsSuccess(response));
  } catch (error) {
    dispatch(getLocationDealsFailed(error));
  }
};

export const loadMoreLocationDeals = (params) => async (dispatch, getState) => {
  dispatch(setIsLoading());
  const { mainFilter, secondaryFilter, locationDeals } = getState();
  const { fromwhere } = mainFilter.searchEngine;
  // const selectedDeparture = fromwhere.find(item => item.active) || fromwhere[0];
  const selectedDepartures = fromwhere.filter(item => item.active).map(dp => dp.id);

  const data = {
    ...formatStaticDealsParams({
      ...params,
      filters: secondaryFilter.data,
    }),
    fromwhere: selectedDepartures.length ? selectedDepartures : [fromwhere[0].id],
    page: locationDeals.pagination.page + 1,
    offset: locationDeals.pagination.page * DEALS_PER_PAGE,
  };

  try {
    const response = await fetchLocationDealsList(data);
    dispatch(loadMoreLocationDealsSuccess(response));
  } catch (error) {
    dispatch(loadMoreLocationDealsFailed(error));
  }
};

export const getSession = () => async (dispatch, getState) => {
  const {
    mainFilter: { searchEngine },
    userSearch,
    secondaryFilter,
    locationDeals,
  } = getState();
  const sort = secondaryFilter.data.sort.find((item) => item.active);

  const [selectedDestination] = searchEngine.where.destinations?.filter(
    ({ active }) => active
  );
  let requestedHotelIds = locationDeals.list
    .slice(0, 20)
    .map((item) => item.id);
  const selectedDepartures = searchEngine.fromwhere.filter(item => item.active).map(dp => dp.id);

  if (requestedHotelIds.length === 0) return;

  if (userSearch.staticDeal?.id) {
    requestedHotelIds = [
      userSearch.staticDeal.id,
      ...requestedHotelIds,
    ]
  }

  if (userSearch.selectedHotel?.hotelId && (!userSearch.staticDeal?.id || (userSearch.staticDeal?.id !== userSearch.selectedHotel?.hotelId))) {
    requestedHotelIds = [
      userSearch.selectedHotel.hotelId,
      ...requestedHotelIds,
    ]
  }

  const params = {
    who: {
      adult: searchEngine.who.adults,
      child: searchEngine.who.children,
      room: searchEngine.who.rooms,
      childAges: searchEngine.who.childAges,
    },
    destination: selectedDestination.id,
    departureDate: searchEngine.when.startDate,
    returnDate: searchEngine.when.endDate,
    priorityHotels: [...requestedHotelIds],
    sort: {
      [sort.label]: sort.value,
    },
    fromwhere: selectedDepartures.length ? selectedDepartures : [searchEngine.fromwhere[0].id],
    specificHotelSearch: userSearch.selectedHotel?.hotelId,
  };

  const isSessionOutdated = (timestamp) => {
    const SESSION_MINUTES_TTL = 30 * 1000 * 60;
    return Date.now() - timestamp <= SESSION_MINUTES_TTL;
  };

  const isSearchEqual = (search) => {
    return (
      JSON.stringify(search) ===
      JSON.stringify({
        date: `${params.departureDate}-${params.returnDate}`,
        rooms: params.who,
        city: params.destination,
        // priorityHotels: params.priorityHotels,
        fromwhere: params.fromwhere,
      })
    );
  };

  let searchHistory = JSON.parse(localStorage.getItem('_searchHistory')) ?? [];
  searchHistory = searchHistory.filter((search) =>
    isSessionOutdated(search.timestamp)
  );

  const searchIndex = searchHistory.findIndex((search) =>
    isSearchEqual(search.params)
  );

  if (searchIndex !== -1 && !(userSearch.selectedHotel?.hotelId || userSearch.staticDeal?.id)) {
    searchHistory[searchIndex].filters &&
      dispatch(setFilters(searchHistory[searchIndex].filters));
    return dispatch(fetchSessionSuccess(searchHistory[searchIndex].session));
  }

  try {
    dispatch(fetchSessionStart());
    dispatch(setIsPricesFetched(false));

    const response = await fetchSessionRequest(params);

    dispatch(fetchSessionSuccess(response));

    localStorage.setItem(
      '_searchHistory',
      JSON.stringify([
        {
          params: {
            date: `${params.departureDate}-${params.returnDate}`,
            rooms: params.who,
            city: params.destination,
            // priorityHotels: params.priorityHotels,
            fromwhere: params.fromwhere,
          },
          timestamp: Date.now(),
          session: response,
        },
        ...searchHistory,
      ])
    );
  } catch (error) {}
};

export const fetchHotelPrices = () => async (dispatch, getState) => {
  const {
    locationDeals,
    secondaryFilter,
    mainFilter,
    userSearch,
  } = getState();
  const { session, isPricesFetched } = locationDeals;

  let priorityHotels = locationDeals.list.slice(0, 20).map((item) => item.id);

  if (userSearch.staticDeal?.id) {
    priorityHotels = [
      userSearch.staticDeal.id,
      ...locationDeals.list.slice(0, 19).map((item) => item.id)
    ]
  }

  if (!session.session_id && !locationDeals.sessionPending) {
    await dispatch(getSession());

    return dispatch(fetchHotelPrices());
  }

  dispatch(setHotelPriceLoading());

  const getHotelPrices = function (session, recursionStep) {
    if (session !== getState().locationDeals.session.session_id) {
      return null;
    }

    let step = recursionStep || 0;

    const params = {
      onTheFlySessionId: session,
      priorityHotels: [...priorityHotels],
      ...prepareSearchRequest({
        mainFilter: mainFilter.searchEngine,
        secondaryFilter: secondaryFilter.data
      }),
      limit: 20,
    };

    // if (step === 0) dispatch(clearHotelPrices());
    if (step > 1) { // previous number was 15
      pushEvent(LOAD_OFFERS_ONTHEFLY_EVENT, { requestCount: step, offersNumber: 0, response: { message: 'Limit of requests is reached' } });
      return dispatch(fetchHotelPricesFailed());
    }

    if (priorityHotels.length < 1) {
      let { locationDeals } = getState();

      priorityHotels = locationDeals.list.slice(0, 20).map((item) => item.id);
      step++;
      return setTimeout(() => {
        getHotelPrices(session, step);
      }, 2000);
    }

    if (step === 0 && !userSearch.selectedHotel?.hotelId) dispatch(setIsPricesFetched(true));

    // dispatch(setHideSoldOutDeals(isPricesFetched));


    fetchLocationDealsPrices(params, isPricesFetched)
      .then((response) => {
        const { success, data } = response;

        if (!success || (success && data.offers.length === 0)) {
          step++;

          if(success && data.pagination && data.pagination.total_offers_pages) {
            dispatch(fetchHotelPricesPaginationSuccess(response));
          }

          return setTimeout(() => {
            getHotelPrices(session, step);
          }, 2000);
        } else {
          dispatch(fetchHotelPricesSuccess(response));
          pushEvent(LOAD_OFFERS_ONTHEFLY_EVENT, { requestCount: step + 1, offersNumber: data.offers.length, response });
        }
      })
      .catch(() => {
        step++;

        return setTimeout(() => {
          getHotelPrices(session, step);
        }, 2000);
      });
  };

  let getHotelPricesSent = false;
  const getPingStatus = function(session, recursionStep) {
    if (session !== getState().locationDeals.session.session_id) {
      return null;
    }
    dispatch(setPingStatusPending());
    let step = recursionStep || 0;
    const currentTimestamp = Date.now();
    const url = `${FETCH_PING_STATUS}?onTheFlySessionId=${session}&tt=${currentTimestamp}`;

    if (step === 0) dispatch(clearHotelPrices());
    if (step > 30) {
      pushEvent(LOAD_OFFERS_ONTHEFLY_EVENT, { requestCount: step, offersNumber: 0, response: { message: 'Limit of requests is reached' } });
      return dispatch(fetchHotelPricesFailed());
    }
    fetch(url)
      .then((res) => res.json())
      .then(response => {
        // if (response && response.offers_ready) {
        if (response && response.offers_ready_first_page) {
          if (response.offers_ready) {
            dispatch(clearPingStatusPending());
          } else {
            step++;
            setTimeout(() => {
              getPingStatus(session, step);
            }, 2000);
          }
          if (!getHotelPricesSent) {
            getHotelPricesSent = true;
            getHotelPrices(session);
          }
        } else {
          step++;
          return setTimeout(() => {
            getPingStatus(session, step);
          }, 2000);
        }
      })
      .catch(() => {
        step++;
        return setTimeout(() => {
          getPingStatus(session, step);
        }, 2000);
      });
  };

  getPingStatus(locationDeals.session.session_id);

  // getHotelPrices(locationDeals.session.session_id);
};


export const loadMoreHotelPrices = (page) => async (dispatch, getState) => {
  dispatch(setHotelPriceLoading());
  const {
    locationDeals,
    secondaryFilter,
    mainFilter,
  } = getState();
  const { session } = locationDeals;
  const priorityHotels = locationDeals.list.slice(0, 20).map((item) => item.id);

  const params = {
    onTheFlySessionId: session.session_id,
    priorityHotels: [...priorityHotels],
    ...prepareSearchRequest({
      mainFilter: mainFilter.searchEngine,
      secondaryFilter: secondaryFilter.data
    }),
    offset: DEALS_PER_PAGE * page,
    limit: DEALS_PER_PAGE,
  };

  try {
    const response = await fetchLocationDealsPrices(params, locationDeals.isPricesFetched);

    dispatch(loadMoreHotelPricesSuccess(response));
    if (response) {
      pushEvent(LOAD_MORE_LOCATION_DEALS_FINISHED_EVENT, { response: response.data });
    }
  } catch (error) {
    dispatch(fetchHotelPricesFailed());
  }
};

export const selectLocationDealsList = (state) => state.locationDeals;
export const selectSessionData = (state) => state.locationDeals.session;

export default locationDealsSlice.reducer;
